import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MoneyInput } from '@/components/ui/money-input';
import { Select } from '@/components/ui/select';
import { useConfirm, usePrompt } from '@/components/DialogProvider';
import { errorMessage } from '@/lib/errors';
import {
  financeEnvelopeMovesQuery,
  financeEnvelopesQuery,
  newOperationId,
  settleStableOperation,
  todayInput,
  useCreateFinanceEnvelope,
  useInvalidateFinance,
  useMoveFinanceEnvelope,
  useReverseFinanceEnvelopeMove,
  useUpdateFinanceEnvelope,
} from '@/lib/finance';
import { formatMinor, toMinor } from '@/lib/money';
import type { FinanceEnvelope, FinanceEnvelopeMove } from '@/api/generated/types.gen';

export interface EnvelopesPanelProps {
  canManage: boolean;
  /** finance_settings.custom_envelopes_enabled — «New envelope» only when on (spec §3.2 p.7). */
  customEnabled: boolean;
  businessTz?: string;
}

/**
 * Envelopes are earmarks over real money (spec 2026-10-21 §2): «these EUR are
 * set aside for X». Real accounts are untouched by anything here. Operating
 * money is a reference figure only — the accounts' EUR on business today
 * minus every envelope; an estimate when a currency has no rate.
 *
 * Only a move made by hand can be reversed here: the server refuses a
 * payment-made move with «reverse the movement instead».
 */
export function EnvelopesPanel({ canManage, customEnabled, businessTz }: EnvelopesPanelProps) {
  const [showInactive, setShowInactive] = useState(false);
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [editing, setEditing] = useState<FinanceEnvelope | 'new' | null>(null);
  const [moving, setMoving] = useState<'in' | 'out' | null>(null);
  const list = useQuery(financeEnvelopesQuery({ includeInactive: showInactive }));
  // An older server answers without `envelopes` (API rollback): it has no
  // /finance/envelope-moves (the feed would 404) and takes no envelope_id on
  // writes, so the panel shows only the «unavailable» line — no feed, no
  // buttons. Widened: the generated type says the array is always there.
  const serverHasEnvelopes = Array.isArray((list.data as { envelopes?: unknown } | undefined)?.envelopes);
  const feed = useQuery({ ...financeEnvelopeMovesQuery(filter), enabled: serverHasEnvelopes });
  const askConfirm = useConfirm();
  const askPrompt = usePrompt();
  const reverse = useReverseFinanceEnvelopeMove();
  const [failure, setFailure] = useState<string | null>(null);
  // Fix round 2 (Critical, no-tech-debt-drift — the re-review found the
  // SAME pattern here as the operation_id fix elsewhere): `reverse` is one
  // hook shared by every row's Reverse button, so a per-call
  // mutate(vars, {onError}) for row A is silently dropped if row B's
  // Reverse fires before A settles (TanStack Query v5 MutationObserver
  // detaches from the previous mutation the moment .mutate() runs again —
  // see settleStableOperation's doc comment, lib/finance.ts). Routed
  // through the same helper for consistency, even though this file has no
  // `clear` step of its own to protect (a reversed move drops off the list
  // via reversedIds, so the key is simply never asked for again) — an
  // operation_conflict still needs the key cleared + the feed invalidated.
  const invalidate = useInvalidateFinance();
  // One key per MOVE, kept for the life of the panel (§5.19). Minting it
  // inside the handler gave every attempt a fresh key, so a retry after a
  // lost response would not replay — it would be stopped by the server's
  // «already reversed», i.e. by a conflict rather than by idempotency, and
  // only because a mirror had already landed. A ref, not state: the key must
  // not change between attempts and nothing renders from it.
  const reverseKeys = useRef(new Map<string, string>());

  // A move that already has its mirror ON THIS PAGE is spent. The feed is
  // paginated, so this is a courtesy, not a guarantee — the server answers
  // 409 already_reversed for the rest.
  const reversedIds = new Set((feed.data?.items ?? []).map((m) => m.reverses_id).filter(Boolean));

  async function onReverse(m: FinanceEnvelopeMove) {
    const ok = await askConfirm({
      title: 'Reverse this envelope move?',
      body: 'A mirror move is written — the original stays on record.',
      destructive: true,
      confirmLabel: 'Reverse',
    });
    if (!ok) return;
    const reason = await askPrompt({ title: 'Why?', label: 'Reason' });
    if (!reason?.trim()) return;
    setFailure(null);
    let key = reverseKeys.current.get(m.id);
    if (!key) {
      key = newOperationId();
      reverseKeys.current.set(m.id, key);
    }
    const result = await settleStableOperation(
      reverse.mutateAsync({ id: m.id, body: { operation_id: key, reason: reason.trim() } }),
      m.id,
      { clear: (id) => reverseKeys.current.delete(id) },
      invalidate,
    );
    if (!result.ok) setFailure(result.message);
  }

  const envelopes = serverHasEnvelopes ? list.data?.envelopes : undefined;
  // A feed filter on an envelope the list no longer holds (a switched-off one,
  // once switched-off envelopes are hidden again) is dropped, so the heading,
  // the empty text and the way to clear the filter stay true. Adjusted during
  // render, not in an effect; kept while the list reloads.
  if (filter !== undefined && envelopes && !envelopes.some((e) => e.id === filter)) setFilter(undefined);
  const active = (envelopes ?? []).filter((e) => e.is_active);
  const moves = feed.data?.items ?? [];
  const filterName = filter ? (envelopes ?? []).find((e) => e.id === filter)?.name : undefined;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-lg font-bold text-[var(--text-heading)]">Envelopes</h2>
        {canManage && serverHasEnvelopes && (
          <div className="flex gap-2">
            {customEnabled && (
              <Button variant="secondary" size="sm" onClick={() => setEditing('new')}>
                New envelope
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => setMoving('in')}>
              Set aside
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setMoving('out')}>
              Release
            </Button>
          </div>
        )}
      </div>

      {list.isLoading && <p className="text-sm text-muted">Loading envelopes…</p>}
      {list.isError && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(list.error)}
        </p>
      )}
      {list.data && !envelopes && (
        <p className="text-sm text-muted">Envelopes are unavailable on this server version.</p>
      )}

      {list.data && envelopes && (
        <>
          <Checkbox
            label="Show switched off"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          {envelopes.length === 0 ? (
            <p className="text-sm text-muted">No envelopes yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[var(--border-hairline)] rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
              {envelopes.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm">
                  <button
                    type="button"
                    className="font-semibold hover:underline"
                    aria-pressed={filter === e.id}
                    onClick={() => setFilter(filter === e.id ? undefined : e.id)}
                  >
                    {e.name}
                  </button>
                  {!e.is_active && <Badge variant="neutral">Off</Badge>}
                  {e.note && <span className="text-muted">{e.note}</span>}
                  <span className="ml-auto tabular-nums">{formatMinor(e.balance_eur_minor, 'EUR')}</span>
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Edit ${e.name}`}
                      onClick={() => setEditing(e)}
                    >
                      Edit
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="text-sm text-muted">
            Operating money ≈{' '}
            <span className="font-semibold tabular-nums text-foreground">
              {/* Required by the contract: a server that leaves it out (or sends
                  no whole number of cents) shows «—», never a made-up 0.00. */}
              {Number.isSafeInteger(list.data.operating_eur_minor)
                ? formatMinor(list.data.operating_eur_minor, 'EUR')
                : '—'}
            </span>
            {list.data.operating_estimated && (
              <> — estimate: no rate for {(list.data.operating_missing_rates ?? []).join(', ')}</>
            )}
          </p>
        </>
      )}

      {failure && (
        <p role="alert" className="text-sm text-destructive">
          {failure}
        </p>
      )}

      {serverHasEnvelopes && (
        <>
          <h3 className="text-sm font-semibold text-[var(--text-heading)]">
            Moves{filterName ? ` — ${filterName}` : ''}
          </h3>
          {feed.isError && (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage(feed.error)}
            </p>
          )}
          {feed.data && moves.length === 0 ? (
            <p className="text-sm text-muted">No envelope moves yet.</p>
          ) : (
            moves.length > 0 && (
              <ul className="flex flex-col divide-y divide-[var(--border-hairline)] rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
                {moves.map((m) => (
                  <li key={m.id} className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm">
                    <span className="text-muted">{m.occurred_on}</span>
                    <Badge variant="gold">{m.envelope_name}</Badge>
                    <span>{moveLabel(m)}</span>
                    <span className="tabular-nums">{formatMinor(m.amount_eur_minor, 'EUR')}</span>
                    {m.reverses_id && <Badge variant="warning">Reversal</Badge>}
                    {m.note && <span className="text-muted">{m.note}</span>}
                    {canManage && !m.movement_id && !m.reverses_id && !reversedIds.has(m.id) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto"
                        onClick={() => void onReverse(m)}
                      >
                        Reverse
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )
          )}
        </>
      )}

      {editing && (
        <EnvelopeDialog envelope={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
      {moving && (
        <MoveDialog direction={moving} envelopes={active} businessTz={businessTz} onClose={() => setMoving(null)} />
      )}
    </section>
  );
}

/**
 * A move a payment made (movement_id set) reads as the payment's side of it;
 * Set aside / Released are the hand-made moves of the panel's own buttons.
 */
function moveLabel(m: FinanceEnvelopeMove): string {
  if (m.movement_id) return m.direction === 'in' ? 'Payment — into envelope' : 'Payment — from envelope';
  return m.direction === 'in' ? 'Set aside' : 'Released';
}

function EnvelopeDialog({ envelope, onClose }: { envelope: FinanceEnvelope | null; onClose: () => void }) {
  const [name, setName] = useState(envelope?.name ?? '');
  const [note, setNote] = useState(envelope?.note ?? '');
  const [sort, setSort] = useState(String(envelope?.sort ?? 0));
  const [isActive, setIsActive] = useState(envelope?.is_active ?? true);
  const [failure, setFailure] = useState<string | null>(null);
  const create = useCreateFinanceEnvelope();
  const update = useUpdateFinanceEnvelope();
  const pending = create.isPending || update.isPending;

  function submit() {
    setFailure(null);
    // PATCH takes the whole envelope — an omitted field goes back to its
    // default — so every field is sent, except a blank note: it is omitted,
    // and so cleared.
    const body = {
      name: name.trim(),
      ...(note.trim() ? { note: note.trim() } : {}),
      sort: Number.parseInt(sort, 10) || 0,
      is_active: isActive,
    };
    // A server refusal (envelope_not_empty, conflict on a taken name, …) is
    // shown as the server worded it.
    const opts = { onSuccess: onClose, onError: (e: unknown) => setFailure(errorMessage(e)) };
    if (envelope) update.mutate({ id: envelope.id, body }, opts);
    else create.mutate(body, opts);
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={envelope ? `Edit ${envelope.name}` : 'New envelope'}
      description="An envelope earmarks EUR over real money. No account is touched."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={name.trim().length === 0} loading={pending}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <Input label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
        <Input label="Sort" type="number" value={sort} onChange={(e) => setSort(e.target.value)} />
        <Checkbox label="Active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        {failure && (
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function MoveDialog({
  direction,
  envelopes,
  businessTz,
  onClose,
}: {
  direction: 'in' | 'out';
  /** Active envelopes only: a switched-off one is never offered (I7). */
  envelopes: FinanceEnvelope[];
  businessTz?: string;
  onClose: () => void;
}) {
  const [envelopeId, setEnvelopeId] = useState(envelopes[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [occurredOn, setOccurredOn] = useState(() => todayInput(businessTz));
  const [note, setNote] = useState('');
  const [operationId] = useState(newOperationId);
  const [failure, setFailure] = useState<string | null>(null);
  const mut = useMoveFinanceEnvelope();

  // Envelopes are EUR-only, so the field's currency is not a choice.
  const minor = toMinor(amount, 'EUR');
  const ready = envelopeId !== '' && minor !== null && minor > 0 && !!occurredOn;
  const label = direction === 'in' ? 'Set aside' : 'Release';

  return (
    <Dialog
      open
      onClose={onClose}
      title={direction === 'in' ? 'Set aside' : 'Release to operating money'}
      description="Envelopes are EUR earmarks. No account is touched."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!ready}
            loading={mut.isPending}
            onClick={() => {
              if (minor === null) return;
              setFailure(null);
              mut.mutate(
                {
                  operation_id: operationId,
                  envelope_id: envelopeId,
                  direction,
                  amount_eur_minor: minor,
                  occurred_on: occurredOn,
                  ...(note.trim() ? { note: note.trim() } : {}),
                },
                { onSuccess: onClose, onError: (e) => setFailure(errorMessage(e)) },
              );
            }}
          >
            {label}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {envelopes.length === 0 ? (
          <p className="text-sm text-muted">No active envelope</p>
        ) : (
          <Select
            label="Envelope"
            value={envelopeId}
            options={envelopes.map((e) => ({ value: e.id, label: e.name }))}
            onChange={(e) => setEnvelopeId(e.target.value)}
          />
        )}
        <MoneyInput label="Amount" currency="EUR" value={amount} onChange={setAmount} />
        <Input
          label="Date"
          type="date"
          value={occurredOn}
          onChange={(e) => setOccurredOn(e.target.value)}
        />
        <Input label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
        {failure && (
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}
